import { loadStorePeriods, loadStores } from "../../../api_calls/campaigns";
import { dbFormatDate, findStoreIds, independentStoreListOptions, storeListOptionsForFilter } from "../../../api_calls/utils";
import { checkUserDetailsFromCookie } from "../../../api_calls/userManagement";

export const STORE_FILTER_MODES = {
  BRAND: "brand",
  CAMPAIGN: "campaign",
  INDEPENDENT: "independent",
  PERIOD_BASED: "periodBased",
};

export const WORKSPACE_OPTIONS_KEY = "workspace_options";
export const ROOT_RETAILER_GROUP_PARAM = "root_retailer_company_name";

export const uniqueStoreNames = (storeNames = []) => [...new Set(storeNames)];

export const toStoreOptions = (storeNames = []) =>
  uniqueStoreNames(storeNames).map((storeName) => ({
    value: storeName,
    label: storeName,
  }));

export const toStoreOptionsWithRootRetailer = (storeNames = [], storeRecords = []) => {
  const storeRecordsByName = new Map();

  if (Array.isArray(storeRecords)) {
    storeRecords.forEach((storeRecord) => {
      if (typeof storeRecord?.name !== "undefined") {
        storeRecordsByName.set(storeRecord.name, storeRecord);
      }
    });
  }

  return uniqueStoreNames(storeNames).map((storeName) => {
    const storeRecord = storeRecordsByName.get(storeName);
    const rootRetailerCompany = storeRecord?.root_retailer_company;
    const rootRetailerCompanyName = rootRetailerCompany?.name;

    return {
      value: storeName,
      label: storeName,
      root_retailer_company_id: rootRetailerCompany?.id,
      root_retailer_company_name:
        typeof rootRetailerCompanyName !== "undefined" && rootRetailerCompanyName !== ""
          ? rootRetailerCompanyName
          : undefined,
    };
  });
};

// Grouped dropdowns read these fields. A store without both id and name stays ungrouped.
export const rootRetailerOptionFields = (storeRecord) => {
  const rootRetailer = storeRecord?.root_retailer_company;
  if (
    typeof rootRetailer?.id === "undefined" ||
    rootRetailer?.id === null ||
    typeof rootRetailer?.name !== "string" ||
    rootRetailer.name === ""
  ) {
    return {};
  }

  return {
    root_retailer_company_id: rootRetailer.id,
    root_retailer_company_name: rootRetailer.name,
  };
};

export const storeOptionsHaveRootRetailerInfo = (storeOptions = []) =>
  storeOptions.length !== 0 &&
  storeOptions.every(
    (storeOption) =>
      typeof storeOption.root_retailer_company_id !== "undefined" &&
      storeOption.root_retailer_company_id !== null &&
      typeof storeOption.root_retailer_company_name !== "undefined" &&
      storeOption.root_retailer_company_name !== ""
  );

const distinctRootRetailerCount = (storeOptions = []) => {
  const retailerIds = new Set();
  storeOptions.forEach((storeOption) => {
    const retailerId = storeOption?.root_retailer_company_id;
    if (typeof retailerId !== "undefined" && retailerId !== null) {
      retailerIds.add(retailerId);
    }
  });
  return retailerIds.size;
};

// Brand stays flat. Hierarchy needs retailer access, a root company on every
// visible store, and more than one retailer. One retailer is a flat list.
export const shouldUseRetailerStoreHierarchy = ({
  isBrand,
  retailerHierarchyAvailable,
  storeOptions,
}) =>
  isBrand !== true &&
  retailerHierarchyAvailable === true &&
  storeOptionsHaveRootRetailerInfo(storeOptions) &&
  distinctRootRetailerCount(storeOptions) > 1;

const normalizeUserType = (userType) =>
  typeof userType !== "undefined" && userType !== null ? userType.toString().toLowerCase() : "";

export const resolveLoggedInUserType = () => {
  // Keep this aligned with BottomNav. Redux can briefly hold stale user_type
  // during tenant/workspace context changes, while userDetail cookie is refreshed.
  const checkUserDetails = checkUserDetailsFromCookie();

  return checkUserDetails?.success === true ? normalizeUserType(checkUserDetails.user_detail?.user_type) : "";
};

export const resolveBehaviorMode = ({ usedFor, fetchListFor }) => {
  if (usedFor === "campaign") return STORE_FILTER_MODES.CAMPAIGN;
  if (fetchListFor === "independent") return STORE_FILTER_MODES.INDEPENDENT;

  return STORE_FILTER_MODES.PERIOD_BASED;
};

export const resolveStoreNames = ({ mode, behaviorMode, usedFor, storeDetails }) => {
  if (mode === STORE_FILTER_MODES.BRAND) {
    return storeDetails?.brand_filters?.impression_filter?.stores
      ? [...storeDetails.brand_filters.impression_filter.stores].sort()
      : [];
  }

  if (behaviorMode === STORE_FILTER_MODES.INDEPENDENT) {
    return independentStoreListOptions();
  }

  return storeListOptionsForFilter(usedFor);
};

export const resolveStoreRecordsForOptions = ({ behaviorMode, storeDetails }) => {
  // Period filters must use store_dates because this is the API response that
  // now carries both date limits and root retailer metadata for those filters.
  if (behaviorMode === STORE_FILTER_MODES.PERIOD_BASED) {
    return storeDetails?.store_dates;
  }

  return storeDetails?.stores_list;
};

export const resolveStoreIds = ({ selectedStoreNames, behaviorMode }) =>
  findStoreIds(selectedStoreNames, behaviorMode === STORE_FILTER_MODES.PERIOD_BASED);

export const resolveDateRangeFromStores = (selectedStoreNames, storeDates = []) => {
  const storeStartDates = [];
  const storeEndDates = [];

  storeDates.forEach((storeInfo) => {
    if (
      selectedStoreNames.includes(storeInfo.name) &&
      typeof storeInfo.start_date !== "undefined" &&
      storeInfo.start_date !== null
    ) {
      storeStartDates.push(dbFormatDate(storeInfo.start_date));
      storeEndDates.push(dbFormatDate(storeInfo.last_date));
    }
  });

  storeStartDates.sort();
  storeEndDates.sort();

  const endDateIndex = storeEndDates.length !== 0 ? parseInt(storeEndDates.length - 1) : "";

  return {
    date_range_start_date: storeStartDates.length !== 0 ? storeStartDates[0] : dbFormatDate(new Date()),
    date_range_end_date: storeEndDates.length !== 0 ? storeEndDates[endDateIndex] : dbFormatDate(new Date()),
  };
};

export const resolveSelectedStoreNames = (selectedOptions, storeNames) => {
  const selectedStoreNames = [];

  selectedOptions.forEach((selectedOption) => {
    if (selectedOption.label === "(All)") {
      selectedStoreNames.push(...uniqueStoreNames(storeNames));
    } else {
      selectedStoreNames.push(selectedOption.label);
    }
  });

  return uniqueStoreNames(selectedStoreNames);
};

export const loadRequestedStores = (behaviorMode, usedFor, rootCompanyId) => {
  if (behaviorMode === STORE_FILTER_MODES.INDEPENDENT) {
    // These dropdowns sit on embeddable dashboards, so scope the store list to the selected retailer.
    return loadStores("campaign", { applyRetailerView: true });
  }

  if (behaviorMode === STORE_FILTER_MODES.PERIOD_BASED) {
    // Period-based filters sit next to the page-level retailer filter on the
    // dashboards, so they are the only ones scoped by root company.
    return loadStorePeriods(usedFor, rootCompanyId);
  }

  return loadStores(usedFor);
};
